import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";

export interface AdminStats {
    todayConsultations: number;
    totalRecords: number;
    todayRecords: number;
    totalDoctors: number;
    totalPatients: number;
    totalAdmins: number;
    pendingConstantes: number;
    totalUnpaid: number;
}

export const useAdminStats = () => {
    return useQuery<AdminStats>({
        queryKey: ['admin', 'stats'],
        queryFn: async () => {
            const endpoints = [
                { name: 'totalRecords', url: API_ENDPOINTS.MEDICAL_RECORDS.LIST + "/count" },
                { name: 'todayRecords', url: API_ENDPOINTS.MEDICAL_RECORDS.LIST + "/count/today" },
                { name: 'doctorsCount', url: `${API_ENDPOINTS.USERS.LIST}/count/DOCTOR` },
                { name: 'totalPatients', url: API_ENDPOINTS.PATIENTS.COUNT },
                { name: 'adminsCount', url: `${API_ENDPOINTS.USERS.LIST}/count/ADMIN` },
                { name: 'totalUnpaid', url: API_ENDPOINTS.PATIENTS.UNPAID_SUM },
                {
                    name: 'pendingConstantesResponse',
                    url: `${API_ENDPOINTS.CONSTANTES.LIST}?todayOnly=false&showConsulted=false&page=1&size=1`
                },
                {
                    name: 'todayConstantesResponse',
                    url: `${API_ENDPOINTS.CONSTANTES.LIST}?todayOnly=true&showConsulted=false&page=1&size=1`
                },
            ];

            const results = await Promise.all(
                endpoints.map(endpoint =>
                    axiosInstance.get(endpoint.url)
                        .then(res => res.data)
                        .catch(err => {
                            console.error(`Error fetching ${endpoint.name}:`, err);
                            return null;
                        })
                )
            );

            const [
                totalRecords,
                todayRecords,
                doctorsCount,
                totalPatients,
                adminsCount,
                totalUnpaid,
                pendingConstantesResponse,
                todayConstantesResponse,
            ] = results;

            const pending =
                pendingConstantesResponse?.totalItems ??
                pendingConstantesResponse?.metadata?.pendingCount ??
                (typeof pendingConstantesResponse === 'number' ? pendingConstantesResponse : 0);

            const todayPending =
                todayConstantesResponse?.totalItems ??
                todayConstantesResponse?.metadata?.pendingCount ??
                (typeof todayConstantesResponse === 'number' ? todayConstantesResponse : 0);

            return {
                todayConsultations: todayPending,
                totalRecords: totalRecords ?? 0,
                todayRecords: todayRecords ?? 0,
                totalDoctors: doctorsCount ?? 0,
                totalPatients: totalPatients ?? 0,
                totalAdmins: adminsCount ?? 0,
                pendingConstantes: pending,
                totalUnpaid: totalUnpaid ?? 0,
            };
        },
        refetchInterval: 30000, // Refresh every 30 seconds
    });
};
